import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { navigate } from "../router.js";
import { cycleTheme, getThemeMode, themeIcon, themeLabel } from "../utils/theme.js";

const DISTRO_LABEL = import.meta.env.VITE_DISTRO_LABEL ?? "Catalog";
const OTHER_CATALOG_URL = import.meta.env.VITE_OTHER_CATALOG_URL ?? "";
const OTHER_DISTRO_LABEL = import.meta.env.VITE_OTHER_DISTRO_LABEL ?? "";
const VULNERABILITIES_ENABLED = import.meta.env.VITE_VULNERABILITIES_ENABLED === "true";

@customElement("app-header")
export class AppHeader extends LitElement {
  @state() private _themeMode = getThemeMode();
  @state() private _catalogHref = this._buildCatalogHref();
  @state() private _path = location.pathname;

  private _onUrlChange = () => {
    this._catalogHref = this._buildCatalogHref();
    this._path = location.pathname;
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("url-change", this._onUrlChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("url-change", this._onUrlChange);
  }

  createRenderRoot() {
    return this;
  }

  private _buildCatalogHref() {
    return `${OTHER_CATALOG_URL}${location.pathname}${location.search}${location.hash}`;
  }

  private _goHome(e: Event) {
    e.preventDefault();
    navigate("/");
    window.dispatchEvent(new Event("app-navigate-home"));
  }

  private _goVulnerabilities(e: Event) {
    e.preventDefault();
    navigate("/vulnerabilities");
  }

  private _toggleTheme() {
    this._themeMode = cycleTheme();
  }

  render() {
    return html`
      <header class="border-b border-hb-border bg-hb-surface/50 backdrop-blur-sm sticky top-0 z-50">
        <div class="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <a href="/" @click=${this._goHome} class="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/hummingbird-logo.svg" alt="Hummingbird" class="h-6 w-6" />
              <span class="text-white font-bold text-base hidden sm:inline">${DISTRO_LABEL} Image Catalog</span>
            </a>
            <span class="text-gray-500 text-sm hidden sm:inline">|</span>
            <a
              href="/"
              @click=${this._goHome}
              class="text-sm transition-colors
                     ${this._path === "/" || this._path.startsWith("/image/")
                       ? "text-white"
                       : "text-gray-400 hover:text-gray-200"}"
            >
              Images
            </a>
            ${VULNERABILITIES_ENABLED
              ? html`
                  <a
                    href="/vulnerabilities"
                    @click=${this._goVulnerabilities}
                    class="text-sm transition-colors
                           ${this._path.startsWith("/vulnerabilities")
                             ? "text-white"
                             : "text-gray-400 hover:text-gray-200"}"
                  >
                    Security
                  </a>
                `
              : ""}
          </div>
          <div class="flex items-center gap-3">
            ${OTHER_CATALOG_URL
              ? html`
                  <a
                    href=${this._catalogHref}
                    class="text-hb-accent hover:text-amber-300 text-sm transition-colors"
                  >
                    ${OTHER_DISTRO_LABEL} &rarr;
                  </a>
                `
              : ""}
            <button
              @click=${this._toggleTheme}
              class="text-gray-400 hover:text-white transition-colors text-lg cursor-pointer"
              title=${themeLabel(this._themeMode)}
            >
              ${themeIcon(this._themeMode)}
            </button>
          </div>
        </div>
      </header>
    `;
  }
}
